import { Button, Stack } from "@mui/joy";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRecoilState, useRecoilValue } from "recoil";
import { userAtom } from "recoil/userAtom";
import { sendMessage } from "utils/webview";
import useIsInappWebview from "hooks/useIsInappWebview";
import useGetEventFromApp from "hooks/useGetEventFromApp";
import { useCallback, useEffect, useState } from "react";
import { USER_DETAIL_ENDPOINT, USER_DOMAIN, userApi } from "api/userApi";
import ButtonWithModal from "components/common/ButtonWithModal";
import { deepLinkData } from "data/deepLinkData";
import { isBrowser } from "react-device-detect";
import { paymentOrderAtom } from "recoil/paymentOrderAtom";
import { PATH } from "routes/paths";
import { useNavigate } from "react-router-dom";
import { axiosInstance } from "../../../../api";
import { useConfirmStoreReviewerPayment } from "hooks/useConfirmStoreReviewerPayment";
import useGetDeviceInfo from "hooks/useGetDeviceInfo";
import useMixpanelWeb from "hooks/useMixpanelWeb";
import usePaymentButtonClickTracking from "hooks/usePaymentButtonClickTracking";
import { trackCapiStandardEvent } from "../../../../analytics/metaCapi";
import { markPaymentClickForLoginMonitor } from "../../../../utils/paymentLoginMonitor";

export default function PaymentCarePassButton() {
  const isInappWebview = useIsInappWebview();

  const navigate = useNavigate();
  const [user, setUser] = useRecoilState(userAtom);
  const paymentOrderState = useRecoilValue(paymentOrderAtom);
  const parsedMessageFromApp = useGetEventFromApp();
  const queryClient = useQueryClient();

  const { appVersion } = useGetDeviceInfo();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isDone, setIsDone] = useState<boolean>(false); //중복 방지
  const [disabled, setDisabled] = useState<boolean>(false);

  const setMixPanel = useMixpanelWeb({
    eventName: "결제 버튼 클릭",
    params: {
      type: `${paymentOrderState?.paymentProvider}`,
      isFirst: `${paymentOrderState?.isFirst}`,
    },
  });

  const { trackPaymentClick } = usePaymentButtonClickTracking({
    user,
    isInappWebview,
    appVersion,
    setMixPanel,
  });

  const {
    mutate: confirmStoreReviewerPayments,
    isLoading: isLoadingConifirmReviewer,
    isSuccess,
    isError,
  } = useConfirmStoreReviewerPayment();

  useQuery(
    [USER_DOMAIN, USER_DETAIL_ENDPOINT],
    useCallback(() => userApi.getDetail(), []),
    {
      enabled:
        !!axiosInstance.defaults.headers["X-Auth-Token"] && !!user?.accessToken,

      onSuccess: (data) => {
        if (data && data.result) {
          //결제 탭 닫기
          if (data.result.subscription?.isSubscribing) {
            const expiryDaysLeft = data.result.subscription?.expiryDaysLeft;
            if (expiryDaysLeft === undefined || expiryDaysLeft > 25) {
              // 넉넉잡아 25일 (원래 만료 20일 전부터 페이지 진입 가능)
              setDisabled(true);
            }

            if (!user?.subscription?.isSubscribing) {
              if (isInappWebview) {
                // 닫기 토스페이먼츠 창 닫기
                sendMessage(
                  JSON.stringify({
                    event: "navigate_back",
                  })
                );
              } else {
                navigate("/", { replace: true });
              }
            }
          }

          setUser((user) => ({
            ...user,
            ...data.result,
          }));
        }
      },
    }
  );

  useEffect(() => {
    if (parsedMessageFromApp?.event === "payment") {
      const status = parsedMessageFromApp.params.status;
      if (status) {
        setIsLoading(false);
        if (status === "success" && !isDone) {
          queryClient.invalidateQueries([USER_DOMAIN, USER_DETAIL_ENDPOINT]);
          sendMessage(
            JSON.stringify({
              event: "navigate_tab",
              params: {
                tabName: "코칭",
              },
            })
          );
          setIsDone(true);
        }
      }
    }
  }, [parsedMessageFromApp]);

  const buttonText = "결제하기";

  const requestPayment = () => {
    if (paymentOrderState) {
      markPaymentClickForLoginMonitor({
        source: "PaymentCarePassButton",
        paymentProvider: paymentOrderState.paymentProvider,
        isInappWebview: !!isInappWebview,
        appVersion,
      });

      if (paymentOrderState?.isStoreReviewer) {
        setIsLoading(true);

        confirmStoreReviewerPayments(
          { orderId: paymentOrderState?.orderId!! },
          {
            onSuccess: () => {
              if (isInappWebview) {
                setIsLoading(false);

                setDisabled(true);

                sendMessage(
                  JSON.stringify({
                    event: "navigate_back",
                  })
                );
              } else {
                // 웹앱이면 메인화면으로
                navigate("/", { replace: true });
              }
            },
          }
        );

        return;
      }

      trackPaymentClick();

      trackCapiStandardEvent(
        "InitiateCheckout",
        {
          externalId: user?.userId,
          email: user?.email,
        },
        undefined,
        undefined,
        true
      );

      if (isInappWebview) {
        setIsLoading(true);

        sendMessage(
          JSON.stringify({
            event: "request_toss_payments",
            params: {
              url: `${window.location.origin}${PATH.tossPayments}`,
            },
          })
        );

        setTimeout(() => {
          setIsLoading(false);
        }, 5000);
      } else {
        navigate("/toss-payments");
      }
    }
  };

  const { mutate: reassignCoach } = useMutation(() => userApi.reassignCoach(), {
    onSuccess: () => {},
  });

  return !isInappWebview &&
    isBrowser &&
    paymentOrderState?.paymentProvider !== "toss_payments" ? (
    <ButtonWithModal
      size={"lg"}
      color={"primary"}
      buttonFullWidth
      buttonText={buttonText ?? ""}
      disabled={disabled}
      title={"앱에서 진행해주세요"}
      textAlign="center"
      content={
        <Stack alignItems={"center"} spacing={1}>
          <img
            src={deepLinkData.payment.qr}
            alt={"empty"}
            className="w-[100px] "
          />
        </Stack>
      }
    />
  ) : user?.isCoachChanged ? (
    <ButtonWithModal
      size={"lg"}
      color={"primary"}
      buttonFullWidth
      buttonText={buttonText ?? ""}
      disabled={disabled}
      loading={isLoading}
      title={"담당 코치가 변경되었어요"}
      subtitle={`담당 코치는 더 이상 디스턴싱과 함께하지 않게 되었어요.`}
      textAlign="center"
      actionText="새 코치 만나기"
      action={() => {
        reassignCoach();
        requestPayment();
      }}
    />
  ) : (
    <Button
      loading={isLoading}
      disabled={disabled}
      onClick={requestPayment}
      size={"lg"}
      color={"primary"}
      sx={{ width: "100%", fontWeight: 700 }}
    >
      {buttonText}
    </Button>
  );
}
